import React, { useEffect, useCallback } from 'react';
import { XMarkIcon, ArrowRightIcon, MegaphoneIcon } from '../Icons/Icons';
import { useTranslate } from 'piral-translate';
import { useReleaseNotesStore } from './releaseNotes.store';
import './release-notes-button.css';

export const ReleaseNotesButton: React.FC = () => {
  const translate = useTranslate();
  const {
    isOpen,
    showIntro,
    isTransitioning,
    showEnterAnimation,
    selectedVersion,
    latestVersion,
    releaseNotes,
    open,
    close,
    showReleaseNotesContent,
    selectVersion,
    checkForNewVersion,
  } = useReleaseNotesStore();

  const currentVersion = process.env.BUILD_PCKG_VERSION;

  // Check for new version on mount
  useEffect(() => {
    checkForNewVersion();
  }, [checkForNewVersion]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  // Listen for custom event from Spotlight
  useEffect(() => {
    window.addEventListener('open-release-notes', open);
    return () => window.removeEventListener('open-release-notes', open);
  }, [open]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        close();
      }
    },
    [close],
  );

  const selectedRelease = releaseNotes.find(r => r.version === selectedVersion) ?? releaseNotes[0] ?? { version: '', date: '', changes: [] as string[] };

  return (
    <>
      {isOpen && (
        <div className="neo-release-notes-backdrop" onClick={handleBackdropClick}>
          <div className={`neo-release-notes-dialog ${showIntro ? 'neo-release-notes-dialog-intro' : ''}`}>
            {showIntro ? (
              <>
                <button type="button" className="neo-release-notes-close neo-release-notes-close-intro" onClick={close}>
                  <XMarkIcon />
                </button>
                <div className={`neo-release-notes-intro ${isTransitioning ? 'neo-release-notes-intro-exit' : ''}`}>
                  <div className="neo-release-notes-intro-icon">
                    <MegaphoneIcon />
                  </div>
                  <h2 className="neo-release-notes-intro-title">{translate('update-available')}</h2>
                  <p className="neo-release-notes-intro-subtitle">{translate('update-description')}</p>
                  <div className="neo-release-notes-intro-version">v{latestVersion}</div>
                  <button type="button" className="neo-release-notes-intro-button" onClick={showReleaseNotesContent}>
                    {translate('view-release-notes')}
                    <ArrowRightIcon />
                  </button>
                </div>
              </>
            ) : (
              <div className={`neo-release-notes-main ${showEnterAnimation ? 'neo-release-notes-main-enter' : ''}`}>
                <div className="neo-release-notes-header">
                  <div className="neo-release-notes-title">
                    <MegaphoneIcon className="neo-release-notes-title-icon" />
                    <span>{translate('release-notes')}</span>
                  </div>
                  <span className="neo-release-notes-version">v{currentVersion}</span>
                  <button type="button" className="neo-release-notes-close" onClick={close}>
                    <XMarkIcon />
                  </button>
                </div>
                <div className="neo-release-notes-body">
                  <div className="neo-release-notes-sidebar">
                    <div className="neo-release-notes-section-header">{translate('versions')}</div>
                    {releaseNotes.map((release, index) => (
                      <button
                        key={release.version}
                        type="button"
                        className={`neo-release-notes-version-item ${selectedVersion === release.version ? 'selected' : ''}`}
                        onClick={() => selectVersion(release.version)}>
                        <div className="neo-release-notes-version-row">
                          <span className="neo-release-notes-version-number">v{release.version}</span>
                          <span className="neo-release-notes-version-date">{release.date}</span>
                        </div>
                        {index === 0 && <span className="neo-release-notes-new-badge">{translate('new')}</span>}
                      </button>
                    ))}
                  </div>
                  <div className="neo-release-notes-content">
                    <div className="neo-release-notes-content-header">
                      <h3 className="neo-release-notes-content-version">v{selectedRelease.version}</h3>
                      <span className="neo-release-notes-content-date">{selectedRelease.date}</span>
                    </div>
                    <ul className="neo-release-notes-list">
                      {selectedRelease.changes.map((change, index) => (
                        <li key={index}>{change}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
